import { useCallback } from 'react';

import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useUploadFilesFromComputer } from '~/hooks/useUploadFilesFromComputer';
import { useUploadFoldersFromComputer } from '~/hooks/useUploadFoldersFromComputer';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateAsset } from '~/utils/permissions/assetPermissions';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

const WORKSPACE_UPLOADS_TRACK_LOCATION = 'workspace-uploads';

export type UseWorkspaceUploadsActionsResult = {
  onUploadFiles: () => void;
  onUploadFolders: (() => void) | undefined;
};

export const useWorkspaceUploadsActions = (): UseWorkspaceUploadsActionsResult => {
  const { uploadFilesFromComputer } = useUploadFilesFromComputer({
    pickDestination: true,
    trackLocation: WORKSPACE_UPLOADS_TRACK_LOCATION,
  });
  const { uploadFoldersFromComputer } = useUploadFoldersFromComputer({
    pickDestination: true,
    trackLocation: WORKSPACE_UPLOADS_TRACK_LOCATION,
  });
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { isStoragePlanRestricted, showStoragePlanRestrictionModal } = useStoragePlanRestriction();

  const canAddBoards = canCreateAsset(workspacePermissions) && canCreateBoard(workspacePermissions);

  const onUploadFiles = useCallback(() => {
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
      return;
    }

    uploadFilesFromComputer();
  }, [isStoragePlanRestricted, showStoragePlanRestrictionModal, uploadFilesFromComputer]);

  const onUploadFolders = useCallback(() => {
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
      return;
    }

    uploadFoldersFromComputer();
  }, [isStoragePlanRestricted, showStoragePlanRestrictionModal, uploadFoldersFromComputer]);

  return {
    onUploadFiles,
    onUploadFolders: canAddBoards ? onUploadFolders : undefined,
  };
};
